import { useState } from "react";
import {
  CalendarDays,
  Users,
  BedDouble,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import CancelBookingModal from "./CancelBookingModal";
import { formatCurrency, formatDate } from "@/lib/utils/formatters";
import type { Booking, BookingStatus } from "@/types/booking";
import { cn } from "@/lib/utils/cn";

// ── Status display config ─────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  BookingStatus,
  {
    label: string;
    badge: "green" | "blue" | "amber" | "red" | "gray";
    icon: React.ElementType;
    message: string;
  }
> = {
  pending: {
    label: "Payment Pending",
    badge: "amber",
    icon: Clock,
    message: "Your payment is being processed.",
  },
  confirmed: {
    label: "Confirmed",
    badge: "green",
    icon: CheckCircle,
    message: "Your booking is confirmed. We look forward to welcoming you!",
  },
  checked_in: {
    label: "Checked In",
    badge: "blue",
    icon: CheckCircle,
    message: "You are currently checked in. Enjoy your stay!",
  },
  checked_out: {
    label: "Checked Out",
    badge: "gray",
    icon: CheckCircle,
    message: "Thank you for staying with us. We hope to see you again!",
  },
  cancelled: {
    label: "Cancelled",
    badge: "red",
    icon: XCircle,
    message: "This booking has been cancelled.",
  },
  refunded: {
    label: "Refunded",
    badge: "gray",
    icon: CheckCircle,
    message: "Your refund has been processed.",
  },
};

const CANCELLABLE_STATUSES: BookingStatus[] = ["confirmed"];

interface BookingDetailCardProps {
  booking: Booking;
  // Fixed: onCancel now explicitly returns Promise<boolean>
  onCancel: (reason: string) => Promise<boolean>;
}

export default function BookingDetailCard({
  booking,
  onCancel,
}: BookingDetailCardProps) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const status = STATUS_CONFIG[booking.status];
  const canCancel = CANCELLABLE_STATUSES.includes(booking.status) && !cancelled;

  // Fixed: properly awaits the boolean result and handles failure
  const handleCancel = async (reason: string) => {
    setCancelling(true);
    setCancelError("");
    try {
      const success = await onCancel(reason);
      if (success) {
        setCancelOpen(false);
        setCancelled(true);
      } else {
        setCancelError(
          "Cancellation failed. Please try again or contact the hotel.",
        );
      }
    } catch {
      setCancelError("Something went wrong. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <div className="space-y-5">
        {/* Status banner */}
        <div
          className={cn(
            "flex items-start gap-3 rounded-xl p-4",
            booking.status === "confirmed" || booking.status === "checked_in"
              ? "bg-metro-green-50"
              : booking.status === "cancelled" || booking.status === "refunded"
                ? "bg-red-50"
                : "bg-amber-50",
          )}
        >
          <status.icon
            size={20}
            className={cn(
              "mt-0.5 shrink-0",
              booking.status === "confirmed" || booking.status === "checked_in"
                ? "text-metro-green-800"
                : booking.status === "cancelled" ||
                    booking.status === "refunded"
                  ? "text-red-500"
                  : "text-amber-500",
            )}
          />
          <div>
            <div className="mb-0.5 flex items-center gap-2">
              <p className="text-sm font-medium text-metro-green-950">
                {status.label}
              </p>
              <Badge label={status.label} variant={status.badge} />
            </div>
            <p className="text-xs text-metro-ink-muted">{status.message}</p>
          </div>
        </div>

        {/* Booking ref */}
        <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-4 py-3">
          <span className="text-xs text-metro-ink-subtle">
            Booking Reference
          </span>
          <span className="font-mono font-semibold text-metro-green-800">
            {booking.bookingRef}
          </span>
        </div>

        {/* Stay details */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
            Stay Details
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 text-sm">
            <div className="flex items-center gap-2.5">
              <BedDouble size={16} className="text-metro-green-800 shrink-0" />
              <div>
                <p className="text-xs text-metro-ink-subtle mb-0.5">Room</p>
                <p className="font-medium text-metro-green-950">
                  {booking.roomTypeName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Users size={16} className="text-metro-green-800 shrink-0" />
              <div>
                <p className="text-xs text-metro-ink-subtle mb-0.5">Guests</p>
                <p className="font-medium text-metro-green-950">
                  {booking.guests.adults}{" "}
                  {booking.guests.adults === 1 ? "Adult" : "Adults"}
                  {booking.guests.children > 0 &&
                    `, ${booking.guests.children} ${booking.guests.children === 1 ? "Child" : "Children"}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <CalendarDays
                size={16}
                className="text-metro-green-800 shrink-0"
              />
              <div>
                <p className="text-xs text-metro-ink-subtle mb-0.5">Check-in</p>
                <p className="font-medium text-metro-green-950">
                  {formatDate(booking.checkIn)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <CalendarDays
                size={16}
                className="text-metro-green-800 shrink-0"
              />
              <div>
                <p className="text-xs text-metro-ink-subtle mb-0.5">
                  Check-out
                </p>
                <p className="font-medium text-metro-green-950">
                  {formatDate(booking.checkOut)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Guest details */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
            Guest Details
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <p className="text-xs text-metro-ink-subtle mb-0.5">Name</p>
              <p className="font-medium text-metro-green-950">
                {booking.guest.firstName} {booking.guest.lastName}
              </p>
            </div>
            <div>
              <p className="text-xs text-metro-ink-subtle mb-0.5">Email</p>
              <p className="font-medium text-metro-green-950">
                {booking.guest.email}
              </p>
            </div>
            <div>
              <p className="text-xs text-metro-ink-subtle mb-0.5">Phone</p>
              <p className="font-medium text-metro-green-950">
                {booking.guest.phone}
              </p>
            </div>
            {booking.guest.specialRequests && (
              <div className="sm:col-span-2">
                <p className="text-xs text-metro-ink-subtle mb-0.5">
                  Special Requests
                </p>
                <p className="font-medium text-metro-green-950">
                  {booking.guest.specialRequests}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Price breakdown */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
            Payment Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-metro-ink-muted">
              <span>
                {formatCurrency(booking.pricing.basePrice)} ×{" "}
                {booking.pricing.nights}{" "}
                {booking.pricing.nights === 1 ? "night" : "nights"}
              </span>
              <span>{formatCurrency(booking.pricing.subtotal)}</span>
            </div>
            <div className="flex justify-between text-metro-ink-muted">
              <span>GST ({booking.pricing.gstRate}%)</span>
              <span>{formatCurrency(booking.pricing.gstAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-metro-green-950 border-t border-gray-100 pt-2">
              <span>Total Paid</span>
              <span>{formatCurrency(booking.pricing.total)}</span>
            </div>
          </div>
        </div>

        {/* Cancellation CTA */}
        {canCancel && (
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 flex items-start gap-3">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-amber-500"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 mb-1">
                Need to cancel?
              </p>
              <p className="text-xs text-amber-700 mb-3">
                Free cancellation up to 48 hours before check-in. After that,
                one night's charge applies.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCancelOpen(true)}
                className="border-amber-400 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
              >
                Request Cancellation
              </Button>
            </div>
          </div>
        )}

        {/* Cancel error */}
        {cancelError && (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
            {cancelError}
          </div>
        )}

        {/* Post-cancel state */}
        {(booking.status === "cancelled" || cancelled) && (
          <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium mb-0.5">Booking Cancelled</p>
            <p className="text-xs">
              If eligible for a refund, it will be processed within 5–7 business
              days. Contact us at{" "}
              <a
                href="mailto:stay@hotelmetropolis.in"
                className="underline underline-offset-2"
              >
                stay@hotelmetropolis.in
              </a>{" "}
              for assistance.
            </p>
          </div>
        )}
      </div>

      <CancelBookingModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelError("");
        }}
        onConfirm={handleCancel}
        loading={cancelling}
      />
    </>
  );
}